import type {
  RemoteMountProps,
  ShellHeaderBreadcrumb,
} from '@wandb/forge-shell-contract';
import {shellPrefixed} from '@wandb/weave/config';
import {useLayoutEffect, useMemo} from 'react';
import {useLocation} from 'react-router';

import {type BreadcrumbPart} from '../components/Breadcrumbs';
import {useNavBreadcrumbParts} from '../components/Search/SearchNav/useNavBreadcrumbParts';
import {isMountedInShell} from '../shellRemote';
import {decodeURIComponentSafe} from '../util/urls';
import {accountLevelHeaderTitle} from './accountLevelHeaderTitle';

/**
 * Publishes SearchNav breadcrumb trails to the Forge shell header when this
 * app is mounted as a remote.
 */
export const ForgeShellHeaderPublisher = ({
  patchHeader,
}: {
  patchHeader: RemoteMountProps['patchHeader'];
}) => {
  const parts = useNavBreadcrumbParts();
  const accountTitle = accountLevelHeaderTitle(useLocation().pathname);
  const shellBreadcrumbs = useMemo(
    () =>
      accountTitle != null
        ? [{label: accountTitle}]
        : breadcrumbPartsToShellHeader(parts),
    [accountTitle, parts]
  );

  useLayoutEffect(() => {
    if (!isMountedInShell() || patchHeader == null) {
      return;
    }
    patchHeader({
      breadcrumbs: shellBreadcrumbs,
      showProductCrumb: accountTitle == null,
    });
  }, [accountTitle, patchHeader, shellBreadcrumbs]);

  return null;
};

export function breadcrumbPartsToShellHeader(
  parts: BreadcrumbPart[]
): ShellHeaderBreadcrumb[] {
  return parts.map((part, index) => {
    const hrefSource = part.to ?? part.href;
    const crumb: ShellHeaderBreadcrumb = {
      label: decodeURIComponentSafe(part.label),
    };
    const isLast = index === parts.length - 1; // last part is current page so it doesn't need to link
    if (!isLast && hrefSource != null) {
      crumb.href = shellPrefixed(hrefSource);
    }
    return crumb;
  });
}
